import type { ResolvedPos } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";
import { CellSelection } from "@tiptap/pm/tables";
import type { Editor } from "@tiptap/react";

const BLOCKED_NODE_NAMES = new Set([
	"aiBlock",
	"mermaidBlock",
	"citation",
	"image",
	"videoEmbed",
	"videoFile",
	"table",
	"tableRow",
	"tableCell",
	"tableHeader",
]);

function nodeChain($pos: ResolvedPos): string[] {
	const names: string[] = [];
	for (let depth = $pos.depth; depth > 0; depth--) {
		names.push($pos.node(depth).type.name);
	}
	return names;
}

export function selectionTouchesBlockedNode(state: EditorState): boolean {
	if (state.selection instanceof CellSelection) return true;

	const { from, to, empty } = state.selection;
	if (empty) {
		return nodeChain(state.doc.resolve(from)).some((name) => BLOCKED_NODE_NAMES.has(name));
	}

	let blocked = false;
	state.doc.nodesBetween(from, to, (node) => {
		if (BLOCKED_NODE_NAMES.has(node.type.name)) {
			blocked = true;
			return false;
		}
		return undefined;
	});
	return blocked;
}

export function isInlineFormattingContext(state: EditorState): boolean {
	const { $from } = state.selection;
	if ($from.parent.type.spec.code) return true;
	if ($from.node().type.name === "code") return true;
	return nodeChain($from).some(
		(name) => name === "codeBlock" || name === "aiBlock" || name === "mermaidBlock",
	);
}

export function shouldShowSelectionBubbleMenu({ editor }: { editor: Editor }): boolean {
	if (!editor.isEditable) return false;
	const { state } = editor;
	if (state.selection.empty) return false;
	if (editor.isActive("codeBlock") || editor.isActive("code")) return false;
	if (selectionTouchesBlockedNode(state)) return false;
	if (isInlineFormattingContext(state)) return false;
	return true;
}

export function isMarkdownPasteBlocked(state: EditorState): boolean {
	const { $from } = state.selection;
	if ($from.parent.type.spec.code) return true;
	if (nodeChain($from).some((name) => BLOCKED_NODE_NAMES.has(name) || name === "codeBlock")) {
		return true;
	}
	return false;
}

/**
 * Разметочные теги: их наличие означает, что в буфере лежит НАСТОЯЩИЙ
 * html-слайс, а не служебная обёртка вокруг простого текста.
 */
const RICH_HTML_TAG = /<(a|strong|b|em|i|u|s|code|pre|h[1-6]|ul|ol|li|table|blockquote|img)[\s>/]/i;

/**
 * Можно ли игнорировать `text/html` из буфера.
 *
 * Зачем: при копировании списка с веб-страницы в буфере лежит И `text/html`
 * (со ссылками, жирным, кодом), И `text/plain`. Разбор plain-текста как
 * markdown терял всё инлайновое форматирование, а `looksLikeMarkdown`
 * срабатывает уже на строке вида «- пункт» — то есть почти на любой вставке из
 * интернета. Перехватывать вставку своим markdown-разбором можно, только когда
 * html-слайса нет вовсе или он не несёт форматирования (редакторы кода и
 * заметок кладут туда `<div>`/`<span>` вокруг сырого markdown).
 */
export function isTrivialHtmlSlice(html: string): boolean {
	if (!html.trim()) return true;
	return !RICH_HTML_TAG.test(html);
}

export function looksLikeMarkdown(text: string): boolean {
	const trimmed = text.trim();
	if (!trimmed) return false;
	return (
		/^#{1,6}\s/m.test(trimmed) ||
		/\*\*[^*]+\*\*/.test(trimmed) ||
		/\[[^\]]+\]\([^)]+\)/.test(trimmed) ||
		/^[-*+]\s/m.test(trimmed) ||
		/^[-*+]\s\[[ xX]\]\s/m.test(trimmed) ||
		/^\d+\.\s/m.test(trimmed) ||
		/^>\s/m.test(trimmed) ||
		/^```/m.test(trimmed)
	);
}

const DRAG_EXCLUDED = new Set([
	"table",
	"tableRow",
	"tableCell",
	"tableHeader",
	"aiBlock",
	"mermaidBlock",
	"citation",
	"image",
	"videoEmbed",
	"videoFile",
]);

export function isDragHandleExcludedNode(nodeName: string): boolean {
	return DRAG_EXCLUDED.has(nodeName);
}
